import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { json } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './transaction-ledger-edit-contribution.css'

import * as Icons from '@icons'
import { Separator } from '@components/shadcn/separator'
import Avatar from '@components/molecules/avatar'
import Button from '@components/atoms/button'
import CurrencyTextField from '@components/molecules/currency-textfield'
import MerchantLogo from '@components/atoms/merchant-logo'
import Spinner from '@components/atoms/spinner'
import TabGroup from '@components/molecules/tab-group'

import { createResponseObj, VirtualCardAPI } from '@apis'
import { toUSD, useCurrencyInput } from '@hooks/inputs'
import { priceFormatter, toPennies } from '@utils/number-helper'
import { isNonEmptyString } from '@utils/type-checkers'
import { flatten } from '@utils/string-helper'
import { queryClient } from '@/lib/queryClient'
import {
    LedgerHelperGroup,
    LedgerEditorSlot,
    buildContributionSlots,
    buildEvenSplitSlots,
    buildLedgerFromContributionTotals,
    buildWeightedSplitSlots,
    getContributionOverMaxUserIds,
    isPledgeClosed,
} from '../helpers'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    canSplit: boolean
    group: Group
    sortedLedger: LedgerLineItem[]
    onDrawerClose: () => void
}

const TransactionLedgerEditContribution: FC<Props> = (props) => {
    const { canSplit, group, sortedLedger, onDrawerClose, ...divProps } = props
    const [submittingLedger, setSubmittingLedger] = useState(false)
    const [manualEdit, setManualEdit] = useState(false)
    const [activeTab, setActiveTab] = useState('Weighted')
    const [contributionSlots, setContributionSlots] = useState<LedgerEditorSlot[]>([])
    const [overMaxUserIds, setOverMaxUserIds] = useState<string[]>([])
    const originalSlotsRef = useRef<LedgerEditorSlot[] | null>(null)

    const spendAmount = group.spendAmount || 0

    const submittable = useMemo(() => {
        const slotsTotal = contributionSlots.reduce((sum, slot) => sum + slot.amount, 0)
        return slotsTotal === spendAmount && overMaxUserIds.length === 0
    }, [contributionSlots, spendAmount, overMaxUserIds])

    useEffect(() => {
        const slots = buildContributionSlots(group as LedgerHelperGroup, sortedLedger)
        setContributionSlots(slots)
        originalSlotsRef.current = slots.map(slot => ({ ...slot }))

        const activeSlots = slots.filter(slot => !isPledgeClosed(group.pledges?.find(pledge => pledge.id === slot.id)))
        const activeAmounts = activeSlots.map(slot => slot.amount)

        if (activeAmounts.length > 0) {
            const minAmount = Math.min(...activeAmounts)
            const maxAmount = Math.max(...activeAmounts)
            if ((maxAmount - minAmount) <= 1) {
                handleTabChange('Equal', slots)
                return
            }
        }

        const weighted = buildWeightedSplitSlots(slots, spendAmount, group.pledges)
        const matchesWeighted = slots.every(slot => {
            const weightedSlot = weighted.find(item => item.id === slot.id)
            return weightedSlot && Math.abs(slot.amount - weightedSlot.amount) <= 1
        })

        if (!matchesWeighted) {
            setActiveTab('Custom')
            setManualEdit(true)
            setOverMaxUserIds(getContributionOverMaxUserIds(slots, group.pledges))
            return
        }

        handleTabChange('Weighted', slots)
    }, [group, sortedLedger, spendAmount])

    function updateContributionSlot(index: number, amount: number) {
        setManualEdit(true)

        setContributionSlots(prevSlots => {
            const nextSlots = [...prevSlots]
            nextSlots[index].isManuallyUpdated = true

            const otherUnchangedSlots = nextSlots.filter((slot, slotIndex) => slotIndex !== index && !slot.isManuallyUpdated && !isPledgeClosed(group.pledges?.find(pledge => pledge.id === slot.id)))
            const otherChangedSlots = nextSlots.filter((slot, slotIndex) => slotIndex !== index && (slot.isManuallyUpdated || isPledgeClosed(group.pledges?.find(pledge => pledge.id === slot.id))))
            const remainingAmount = Math.max(0, spendAmount - (otherChangedSlots.reduce((sum, slot) => sum + slot.amount, 0) + amount))
            const redistributedAmount = otherUnchangedSlots.length > 0 ? Math.round(remainingAmount / otherUnchangedSlots.length) : 0

            const updatedSlots = nextSlots.map((slot, slotIndex) => {
                if (slotIndex === index) return { ...slot, amount }
                if (!slot.isManuallyUpdated && !isPledgeClosed(group.pledges?.find(pledge => pledge.id === slot.id))) return { ...slot, amount: redistributedAmount }
                return slot
            })

            setOverMaxUserIds(getContributionOverMaxUserIds(updatedSlots, group.pledges))
            return updatedSlots
        })
    }

    function handleTabChange(tab: string, baseSlots?: LedgerEditorSlot[]) {
        setActiveTab(tab)

        const sourceSlots = (baseSlots || originalSlotsRef.current || contributionSlots).map(slot => ({ ...slot, isManuallyUpdated: false }))
        let proposed = sourceSlots

        if (tab === 'Weighted') {
            proposed = buildWeightedSplitSlots(sourceSlots, spendAmount, group.pledges)
            setManualEdit(false)
        } else if (tab === 'Equal') {
            proposed = buildEvenSplitSlots(sourceSlots, spendAmount, group.pledges)
            setManualEdit(false)
        } else {
            proposed = buildContributionSlots(group as LedgerHelperGroup, sortedLedger)
            setManualEdit(true)
        }

        setOverMaxUserIds(getContributionOverMaxUserIds(proposed, group.pledges))
        setContributionSlots(proposed)
    }

    async function handleSubmit() {
        setSubmittingLedger(true)

        const groupId = group.id
        if (!isNonEmptyString(groupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId })}`
        })

        const nextLedger = buildLedgerFromContributionTotals({
            group,
            sortedLedger,
            contributionSlots,
            
        })

        await VirtualCardAPI.UpdateGroupRequest.fetch(group.id, {
            targetAmount: group.targetAmount,
            ledger: nextLedger.map(item => ({
                id: item.transactionId,
                breakdown: item.breakdown.map(entry => ({
                    id: entry.id,
                    amount: Math.round(entry.amount),
                }))
            }))
        }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })
            onDrawerClose()

            return json('success', { status: 200 })
        }).catch((error) => {
            console.error(error)
        })

        setSubmittingLedger(false)
    }

    return (
        <div className='flex flex-col gap-4 transaction-ledger-edit-contribution' {...divProps}>
            <div className='flex flex-col items-center gap-4'>
                <MerchantLogo merchantCategory={'unknown'} fill={true} variant="rounded" />
                <div className='flex flex-col items-center gap-2.5'>
                    <div className='flex flex-col items-center'>
                        <h3>Total Cost</h3>
                        <p className='text-body-2 text-gray-500'>Adjust everyone's share across all transactions</p>
                    </div>
                    <h1>{priceFormatter.format(spendAmount / 100)}</h1>
                </div>
            </div>

            {canSplit && <>
                <Separator />
                <TabGroup
                    tabs={[{ name: 'Weighted' }, { name: 'Equal' }, { name: 'Custom' }]}
                    activeTab={activeTab}
                    light={true}
                    onTabChange={handleTabChange}
                />
                {activeTab === 'Weighted' && (
                    <div className="flex flex-row items-center gap-4 info-box bg-blue-100 p-4 rounded-[0.625rem] border border-blue-100">
                        <Icons.Split tint="var(--splitcard-blue-700)" width="1.5rem" height="1.5rem" />
                        <p className="text-body-2 text-blue-700 m-0">Splits are automatically calculated from each person's contribution, then redistributed across transactions oldest to newest.</p>
                    </div>
                )}
            </>}

            {activeTab === 'Custom' && contributionSlots.length > 1 && (
                (() => {
                    const currentSlotsTotal = contributionSlots.reduce((sum, slot) => sum + slot.amount, 0)
                    const diff = currentSlotsTotal - spendAmount

                    if (diff > 0) {
                        return (
                            <div className="flex flex-row items-center justify-between info-box bg-red-100 py-3 px-4 rounded-[0.625rem] border border-red-100">
                                <p className="text-body-2 text-red-500 font-bold m-0">Amount must match the total</p>
                                <p className="text-body-2 text-red-500 font-bold m-0">Over: {priceFormatter.format(diff / 100)}</p>
                            </div>
                        )
                    }

                    if (diff < 0) {
                        return (
                            <div className="flex flex-row items-center justify-between info-box bg-red-100 py-3 px-4 rounded-[0.625rem] border border-red-100">
                                <p className="text-body-2 text-red-500 font-bold m-0">Amount must match the total</p>
                                <p className="text-body-2 text-red-500 font-bold m-0">Left: {priceFormatter.format(Math.abs(diff) / 100)}</p>
                            </div>
                        )
                    }

                    return (
                        <div className="flex flex-row items-center justify-between info-box bg-green-100 py-3 px-4 rounded-[0.625rem] border border-green-100">
                            <p className="text-body-2 text-green-700 font-bold m-0">Perfectly balanced</p>
                            <Icons.CheckSmall tint="var(--splitcard-green-700)" width="1.2rem" height="1.2rem" />
                        </div>
                    )
                })()
            )}

            <div className='flex flex-col gap-4 items'>
                {contributionSlots.map((slot, index) => (
                    <ContributionSlotCell
                        key={`${slot.id}-${index}`}
                        activeTab={activeTab}
                        canSplit={canSplit}
                        group={group}
                        index={index}
                        overMaxUserIds={overMaxUserIds}
                        slot={slot}
                        spendAmount={spendAmount}
                        submittingLedger={submittingLedger}
                        totalSlots={contributionSlots}
                        onContributionUpdate={updateContributionSlot}
                    />
                ))}
            </div>

            {canSplit && <div className="flex flex-row items-end" style={{ flex: 1, gap: '0.625rem', display: 'grid', gridAutoFlow: 'column' }}>
                <Button theme="dark" disabled={!submittable || submittingLedger || (!manualEdit && activeTab === 'Custom')} onClick={handleSubmit}>{submittingLedger ? <Spinner indicator='circle' style={{ margin: 'auto' }} /> : 'Save Split'}</Button>
            </div>}
        </div>
    )
}

interface ContributionSlotCellProps {
    activeTab: string
    canSplit: boolean
    group: Group
    index: number
    overMaxUserIds: string[]
    slot: LedgerEditorSlot
    spendAmount: number
    submittingLedger: boolean
    totalSlots: LedgerEditorSlot[]
    onContributionUpdate: (index: number, amount: number) => void
}

const ContributionSlotCell: FC<ContributionSlotCellProps> = ({ activeTab, canSplit, group, index, overMaxUserIds, slot, spendAmount, submittingLedger, totalSlots, onContributionUpdate }) => {
    const { user } = useUser()
    const slotPledge = group.pledges?.find(pledge => pledge.id === slot.id)
    const slotPledgeClosed = isPledgeClosed(slotPledge)
    const availableFunds = Math.max(0, (slotPledge?.amount || 0) - slot.amount)
    const isOverMax = overMaxUserIds.includes(slot.userId || '')
    const [_, slotAmountProps, setSlotAmount] = useCurrencyInput({
        initValue: !!slot.amount ? toUSD(`${slot.amount / 100}`, undefined, undefined, true) : '0',
        min: 0,
    })
    const slotsTotal = totalSlots.reduce((sum, item) => sum + item.amount, 0)


    useEffect(() => {
        if (slot.isManuallyUpdated) return
        setSlotAmount(toUSD(`${slot.amount / 100}`, undefined, undefined, true))
    }, [setSlotAmount, slot.amount, slot.isManuallyUpdated])

    function handleUpdate(e: React.ChangeEvent<HTMLInputElement>) {
        const amount = e.target.value
        setSlotAmount(amount)
        onContributionUpdate(index, toPennies(amount))
    }

    const stakePercentage = useMemo(() => {
        if (spendAmount > 0) return Math.round((slot.amount / spendAmount) * 100)
        return 0
    }, [slot.amount, spendAmount])

    return (
        <div className={flatten('pledge', isOverMax ? 'over-max' : '')}>
            <Avatar user={{ ...slot, imageUrl: slot.imageUrl || undefined, givenName: slot.givenName || undefined, familyName: slot.familyName || undefined }} />
            <div className="flex flex-col justify-center gap-2" style={{ flex: 2 }}>
                <p className='text-body'><strong>{slot.userId === user?.id ? `You (${slot.givenName || ''} ${slot.familyName || ''})`.trim() : `${slot.givenName || ''} ${slot.familyName || ''}`.trim()}</strong></p>
                <div className="flex flex-row items-center gap-2" style={{ marginTop: '0.2rem' }}>
                    {!slotPledgeClosed
                        ? <div style={{ backgroundColor: 'var(--splitcard-gray-100)', padding: '0.125rem 0.5rem', borderRadius: '1rem' }}>
                            <p className='text-body-2' style={{ fontSize: '0.8rem', color: 'var(--splitcard-grey-600)' }}>
                                Bal: <span style={{ color: availableFunds <= 0 ? 'var(--splitcard-red-500)' : '', fontWeight: availableFunds <= 0 ? 'bold' : '' }}>{priceFormatter.format(availableFunds / 100)}</span>
                            </p>
                        </div>
                        : <p className='text-body-2 text-red-600'><strong>Left Group</strong></p>
                    }
                </div>
            </div>
            <div className='flex flex-col gap-2 items-end flex-1'>
                <CurrencyTextField
                    {...slotAmountProps}
                    invalid={slotsTotal > 0 && slotsTotal !== spendAmount}
                    disabled={submittingLedger || !canSplit || activeTab !== 'Custom' || slotPledgeClosed}
                    onChange={handleUpdate}
                />
                <div style={{ backgroundColor: 'var(--splitcard-gray-100)', padding: '0.125rem 0.5rem', borderRadius: '1rem', width: '100%', textAlign: 'center' }}>
                    <p className='text-body-2' style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--splitcard-grey-500)' }}>{stakePercentage}% stake</p>
                </div>
            </div>
        </div>
    )
}

export default TransactionLedgerEditContribution
